import { FoodItem } from "../../types";
import { RawCategory } from "./categories";

/*
 * Catalogue search.
 *
 * The whole catalogue is already in memory from /product/all, so this is a
 * local scan — no request, no debounce needed. Filtering ~750 items costs well
 * under a millisecond, and going to the server would only add latency.
 */

const normalise = (v: unknown) =>
  String(v ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/**
 * Ranked search over product title, description and the display name of the
 * category it sits in — so "pizza" finds items filed under "Cafe Kulachi Pizza"
 * even when the product itself is named "Fajita Special".
 */
export const searchProducts = (
  foodItems: FoodItem[],
  categories: RawCategory[],
  query: string
): FoodItem[] => {
  const terms = normalise(query).split(" ").filter(Boolean);
  if (!terms.length) return foodItems || [];

  const categoryName: Record<string, string> = {};
  (categories || []).forEach((c) => {
    if (c?.categoryType) categoryName[c.categoryType] = normalise(c.title);
  });

  const scored: { item: FoodItem; score: number }[] = [];

  for (const item of foodItems || []) {
    const title = normalise(item?.title);
    const desc = normalise(item?.description);
    const cat = categoryName[(item as any)?.category] || "";

    let score = 0;
    // Every term has to land somewhere, otherwise "chicken pizza" would match
    // anything chicken-flavoured.
    const matchedAll = terms.every((t) => {
      if (title.startsWith(t)) { score += 100; return true; }
      if (title.includes(t))   { score += 60;  return true; }
      if (cat.includes(t))     { score += 25;  return true; }
      if (desc.includes(t))    { score += 10;  return true; }
      return false;
    });

    if (matchedAll) scored.push({ item, score });
  }

  return scored
    .sort((a, b) => b.score - a.score || normalise(a.item?.title).localeCompare(normalise(b.item?.title)))
    .map((s) => s.item);
};
